import { useCallback, useEffect, useMemo } from 'react'
import SearchForm from '@components/SearchForm'
import { useVesselSearch, useVessels } from '@hooks'
import { LoadingStatus } from '@components/Shared'
import { vesselStoreService } from '@services'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useLanguage } from '@i18n'
import type { Vessel } from '@app-types/index'
import './Vessels.css'

const certificateNumberPattern = /^([A-Z0-9]{1,7})-([A-Z0-9]{2,7})-([A-Z0-9]{2})-([A-Z0-9]{7})$/i

const getCertificateNumberSearch = (searchTerm: string) => {
  const match = searchTerm.match(certificateNumberPattern)

  if (!match) {
    return null
  }

  return {
    normalizedValue: searchTerm.toUpperCase(),
    type: match[2].toUpperCase(),
  }
}

const getCertificateForSearch = (
  vessel: Vessel,
  certificateSearch: ReturnType<typeof getCertificateNumberSearch>,
  searchTerm: string
) => {
  const normalizedSearchTerm = searchTerm.trim().toUpperCase()

  if (!certificateSearch && !normalizedSearchTerm) {
    return null
  }

  const certificates = vessel.certificates || []

  const exactMatch = certificates.find(
    (certificate) =>
      certificate.seqNo?.toUpperCase() === normalizedSearchTerm ||
      certificate.uuid?.toUpperCase() === normalizedSearchTerm
  )

  if (exactMatch) {
    return exactMatch
  }

  if (!certificateSearch) {
    return null
  }

  return (
    (certificates.length === 1 ? certificates[0] : null) ||
    certificates.find((certificate) => certificate.type?.toUpperCase() === certificateSearch.type) ||
    null
  )
}

export const Vessels = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { t } = useLanguage()
  const searchTerm = searchParams.get('query')?.trim() || ''
  const certificateSearch = useMemo(() => getCertificateNumberSearch(searchTerm), [searchTerm])
  const apiSearchTerm = certificateSearch?.normalizedValue || searchTerm
  const { vessels, removeVessel, loading, error } = useVessels()
  const {
    results: searchResults,
    loading: searchLoading,
    error: searchError,
  } = useVesselSearch(apiSearchTerm, false)
  const vesselCount = vessels.length
  const isSearching = searchTerm.length > 0
  const searchResultCount = searchResults.length

  const openSearchResult = useCallback((vessel: Vessel) => {
    vesselStoreService.setVessel(vessel)

    const certificate = getCertificateForSearch(vessel, certificateSearch, searchTerm)
    if (certificate?.uuid) {
      navigate(`/vessels/vessel/${vessel.vesselID}/${certificate.uuid}`)
      return
    }

    navigate(`/vessels/vessel/${vessel.vesselID}`)
  }, [certificateSearch, navigate, searchTerm])

  useEffect(() => {
    if (!isSearching || searchLoading || searchResultCount !== 1) {
      return
    }

    openSearchResult(searchResults[0])
  }, [isSearching, openSearchResult, searchLoading, searchResultCount, searchResults])

  if (isSearching) {
    if (searchLoading) {
      return <LoadingStatus className="vessels vessels--status" label={t('vessels.searchingFor', { searchTerm })} />
    }

    if (searchError) {
      return (
        <section className="vessels">
          <div className="vessels__header">
            <SearchForm />
          </div>
          <div className="vessels--error" role="alert">{t('vessels.searchFailed')}</div>
        </section>
      )
    }

    return (
      <section className="vessels">
        <div className="vessels__header">
          <SearchForm />
        </div>

        {searchResultCount === 0 ? (
          <div className="vessels--error" role="status">{t('vessels.noResultsFor', { searchTerm })}</div>
        ) : (
          <div className="vessels__panel">
            <table className="vessels__table vessels__table--search">
              <caption className="visually-hidden">{t('vessels.searchResultsCaption', { searchTerm })}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('common.vesselName')}</th>
                  <th scope="col">{t('common.imoNumber')}</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map((vessel) => (
                  <tr
                    key={vessel.vesselID}
                    className="vessels__table-row"
                    tabIndex={0}
                    onClick={() => openSearchResult(vessel)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') {
                        return
                      }

                      event.preventDefault()
                      openSearchResult(vessel)
                    }}
                  >
                    <td>
                      <span className="vessels__name">{vessel.vesselName}</span>
                    </td>
                    <td>{vessel.imoNumber || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    )
  }

  if (loading) {
    return <LoadingStatus className="vessels vessels--status" label={t('vessels.loading')} />
  }

  if (error) {
    return <div className="vessels vessels--error">{t('vessels.errorLoading', { message: error.message })}</div>
  }

  return (
    <section className="vessels">
      <div className="vessels__header">
        <SearchForm />
      </div>

      <div className="vessels__panel">
        {vesselCount === 0 ? (
          <div className="vessels__empty">
            <img
              className="vessels__empty-illustration"
              src="/assets/ship_front.svg"
              alt=""
              aria-hidden="true"
            />
            <h2>{t('vessels.emptySavedTitle')}</h2>
            <p>{t('vessels.searchForStart')}</p>
          </div>
        ) : (
          <table className="vessels__table">
            <caption className="visually-hidden">{t('vessels.offlineCaption')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('common.vesselName')}</th>
                <th scope="col">{t('common.imoNumber')}</th>
                <th scope="col">
                  <span className="vessels__remove-heading-label">{t('common.remove')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {vessels.map((vessel) => (
                <tr key={vessel.vesselID}>
                  <td>
                    <Link to={`/vessels/vessel/${vessel.vesselID}`} className="vessels__name">
                      {vessel.vesselName}
                    </Link>
                  </td>
                  <td>{vessel.imoNumber || '-'}</td>
                  <td>
                    <button
                      className="vessels__remove"
                      type="button"
                      aria-label={t('vessels.removeAriaLabel', { vesselName: vessel.vesselName })}
                      onClick={() => {
                        void removeVessel(vessel.vesselID)
                      }}
                    >
                      <span className="vessels__icon vessels__icon--circle-cross" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </section>
  )
}

export default Vessels
